// src/components/pages/Foam/charts/ZonesChart.tsx
import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Legend,
  AreaChart,
  Area,
  Line,
} from "recharts";
import { Box } from "@mui/material";
import {
  getClickedTMs,
  renderMarkers,
  ChartBandZoomButton,
  compactTooltip,
  axisTickFormatter,
  fmtDateTime,
} from "../../../common/chartZoom";

const COLORS = [
  "#1976d2",
  "#2e7d32",
  "#d32f2f",
  "#6a1b9a",
  "#ff8f00",
  "#0097a7",
  "#455a64",
  "#8d6e63",
  "#388e3c",
  "#7b1fa2",
  "#c62828",
  "#0277bd",
  "#f9a825",
  "#5d4037",
  "#1b5e20",
  "#283593",
  "#ad1457",
];

export type ZonesChartProps = {
  data: any[];
  selected: string[]; // zone keys in data rows
  domain: [number, number] | null;
  markers?: number[];
  onChartClick?: (t: number) => void;
  onZoom?: () => void;
  zoneLabel?: (k: string) => string;
};

const defaultLabel = (k: string) => k.replace("zone_", "Zone ");

const ZonesChart: React.FC<ZonesChartProps> = ({
  data,
  selected,
  domain,
  markers = [],
  onChartClick,
  onZoom,
  zoneLabel = defaultLabel,
}) => {
  const handleClick = (state: any) => {
    if (!onChartClick) return;
    const t = getClickedTMs(state);
    if (t != null) onChartClick(t);
  };

  const sel = selected.length ? selected : ["zone_1"];
  if (sel.length === 1) {
    const key = sel[0];
    return (
      <Box sx={{ height: 340, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ left: 60, right: 8, top: 10, bottom: 8 }}
            onClick={handleClick}
          >
            <defs>
              <linearGradient id="zoneGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1976d2" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#1976d2" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="tMs"
              domain={domain ?? (["auto", "auto"] as any)}
              tickFormatter={axisTickFormatter(domain)}
              minTickGap={20}
            />
            <YAxis domain={[0, 200]} width={56} />
            <RTooltip content={compactTooltip((t) => fmtDateTime(Number(t)))} />
            <Area
              type="monotone"
              dataKey={key}
              name={zoneLabel(key)}
              stroke="#1976d2"
              strokeWidth={2}
              fill="url(#zoneGrad)"
              dot={false}
              isAnimationActive={false}
            />
            {renderMarkers(markers)}
          </AreaChart>
        </ResponsiveContainer>
        <ChartBandZoomButton domain={domain} markers={markers} onZoom={onZoom} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 340, position: "relative" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ left: 60, right: 8, top: 10, bottom: 8 }}
          onClick={handleClick}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="tMs"
            domain={domain ?? (["auto", "auto"] as any)}
            tickFormatter={(t) => fmtDateTime(Number(t))}
            minTickGap={20}
          />
          <YAxis domain={[0, 200]} width={56} />
          <RTooltip content={compactTooltip((t) => fmtDateTime(Number(t)))} />
          <Legend />
          {sel.map((z, i) => (
            <Line
              key={z}
              type="monotone"
              dataKey={z}
              name={zoneLabel(z)}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
          {renderMarkers(markers)}
        </LineChart>
      </ResponsiveContainer>
      <ChartBandZoomButton domain={domain} markers={markers} onZoom={onZoom} />
    </Box>
  );
};

export default React.memo(ZonesChart);
